/**
 * sl-3yai.2.2: MarketplaceService
 *
 * Reactive service layer for marketplace + copilot features.
 * Delegates API calls to WorkflowClientService and manages reactive stores
 * for UI consumption via LiveData.
 */

import { Service } from '@toeverything/infra';
import { LiveData } from '@toeverything/infra';

import type {
  Category,
  ClassificationRuleDTO,
  ClassificationSuggestion,
  CompatibilityEvaluation,
  CompatibilityIssue,
  ComplianceState,
  CopilotMigrationResult,
  DiscoveryResult,
  ImportOptions,
  ImportProgress,
  ImportResult,
  WorkflowContext,
  WorkflowEngineNode,
  WorkflowTemplate,
} from './marketplace-types';
import type { WorkflowClientService } from './workflow-client';

export class MarketplaceService extends Service {
  constructor(private readonly workflowClient: WorkflowClientService) {
    super();
  }

  // ===========================================================================
  // Reactive Stores
  // ===========================================================================

  readonly templates$ = new LiveData<WorkflowTemplate[]>([]);
  readonly categories$ = new LiveData<Category[]>([]);
  readonly searchQuery$ = new LiveData<string>('');
  readonly selectedTemplate$ = new LiveData<WorkflowTemplate | null>(null);
  readonly complianceState$ = new LiveData<ComplianceState | null>(null);
  readonly discoveryResult$ = new LiveData<DiscoveryResult | null>(null);
  readonly importProgress$ = new LiveData<ImportProgress | null>(null);
  readonly isLoading$ = new LiveData<boolean>(false);
  readonly error$ = new LiveData<Error | null>(null);

  // ===========================================================================
  // Core Marketplace Actions
  // ===========================================================================

  /**
   * Search templates and update reactive stores.
   */
  async searchTemplates(
    query: string,
    category?: string,
    page?: number
  ): Promise<void> {
    this.isLoading$.next(true);
    this.error$.next(null);

    try {
      this.searchQuery$.next(query);
      const result = await this.workflowClient.searchTemplates(
        query,
        category,
        page
      );
      this.templates$.next(result.items);
    } catch (error) {
      this.error$.next(
        error instanceof Error ? error : new Error('Failed to search templates')
      );
    } finally {
      this.isLoading$.next(false);
    }
  }

  /**
   * Fetch all categories and update reactive store.
   */
  async fetchCategories(): Promise<void> {
    this.isLoading$.next(true);
    this.error$.next(null);

    try {
      const categories = await this.workflowClient.fetchCategories();
      this.categories$.next(categories);
    } catch (error) {
      this.error$.next(
        error instanceof Error ? error : new Error('Failed to fetch categories')
      );
    } finally {
      this.isLoading$.next(false);
    }
  }

  /**
   * Analyze a marketplace template and derive compliance state.
   */
  async analyzeTemplate(templateId: string): Promise<CompatibilityEvaluation> {
    this.isLoading$.next(true);
    this.error$.next(null);

    try {
      const evaluation = await this.workflowClient.analyzeTemplate(templateId);
      this.complianceState$.next(this.deriveComplianceState(evaluation));
      return evaluation;
    } catch (error) {
      this.error$.next(
        error instanceof Error ? error : new Error('Failed to analyze template')
      );
      throw error;
    } finally {
      this.isLoading$.next(false);
    }
  }

  /**
   * Analyze an existing workflow and derive compliance state.
   */
  async analyzeExistingWorkflow(
    workflowId: string
  ): Promise<CompatibilityEvaluation> {
    this.isLoading$.next(true);
    this.error$.next(null);

    try {
      const evaluation =
        await this.workflowClient.analyzeExistingWorkflow(workflowId);
      this.complianceState$.next(this.deriveComplianceState(evaluation));
      return evaluation;
    } catch (error) {
      this.error$.next(
        error instanceof Error ? error : new Error('Failed to analyze workflow')
      );
      throw error;
    } finally {
      this.isLoading$.next(false);
    }
  }

  /**
   * Import a template with progress tracking via importProgress$.
   * Does NOT touch isLoading$ — that drives StoreView's loading spinner
   * and would cause visible flicker. Import state is tracked separately
   * via importProgress$.
   */
  async importTemplate(
    templateId: string,
    options: ImportOptions
  ): Promise<ImportResult> {
    this.error$.next(null);
    this.importProgress$.next({
      step: 'analyzing',
      progress: 10,
      message: 'Analyzing template compatibility...',
    });

    try {
      this.importProgress$.next({
        step: 'importing',
        progress: 40,
        message: 'Importing template...',
      });

      const result = await this.workflowClient.importTemplate(
        templateId,
        options
      );

      if (result.fixesApplied > 0) {
        this.importProgress$.next({
          step: 'fixing',
          progress: 80,
          message: `Applied ${result.fixesApplied} auto-fixes...`,
        });
      }

      await this.workflowClient.fetchWorkflows();

      this.importProgress$.next({
        step: 'complete',
        progress: 100,
        message: `Successfully imported "${result.name}"`,
      });

      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Import failed';
      this.importProgress$.next({
        step: 'error',
        progress: 0,
        message: errorMessage,
      });
      this.error$.next(
        error instanceof Error ? error : new Error(errorMessage)
      );
      throw error;
    }
  }

  /**
   * Re-analyze an existing workflow (alias for analyzeExistingWorkflow).
   */
  async recheckWorkflow(workflowId: string): Promise<CompatibilityEvaluation> {
    return this.analyzeExistingWorkflow(workflowId);
  }

  /**
   * Run schema discovery on a workflow and update stores.
   */
  async runDiscovery(workflowId: string): Promise<DiscoveryResult> {
    this.isLoading$.next(true);
    this.error$.next(null);

    try {
      const result = await this.workflowClient.runDiscovery(workflowId);
      this.discoveryResult$.next(result);
      return result;
    } catch (error) {
      this.error$.next(
        error instanceof Error ? error : new Error('Discovery failed')
      );
      throw error;
    } finally {
      this.isLoading$.next(false);
    }
  }

  /**
   * Get compliance state for a workflow (analyze + derive).
   */
  async getComplianceState(workflowId: string): Promise<ComplianceState> {
    this.isLoading$.next(true);
    this.error$.next(null);

    try {
      const evaluation =
        await this.workflowClient.analyzeExistingWorkflow(workflowId);
      const state = this.deriveComplianceState(evaluation);
      this.complianceState$.next(state);
      return state;
    } catch (error) {
      this.error$.next(
        error instanceof Error
          ? error
          : new Error('Failed to get compliance state')
      );
      throw error;
    } finally {
      this.isLoading$.next(false);
    }
  }

  // ===========================================================================
  // Copilot AI Actions
  // ===========================================================================

  /**
   * Classify an unknown node using AI copilot.
   */
  async classifyNodeWithAI(
    node: WorkflowEngineNode,
    context: WorkflowContext
  ): Promise<ClassificationSuggestion> {
    this.isLoading$.next(true);
    this.error$.next(null);

    try {
      return await this.workflowClient.classifyNodeWithAI(node, context);
    } catch (error) {
      this.error$.next(
        error instanceof Error ? error : new Error('Failed to classify node')
      );
      throw error;
    } finally {
      this.isLoading$.next(false);
    }
  }

  /**
   * Accept a classification suggestion, creating a persistent rule.
   */
  async acceptClassificationSuggestion(
    suggestion: ClassificationSuggestion
  ): Promise<ClassificationRuleDTO> {
    this.isLoading$.next(true);
    this.error$.next(null);

    try {
      return await this.workflowClient.acceptClassificationSuggestion(
        suggestion
      );
    } catch (error) {
      this.error$.next(
        error instanceof Error
          ? error
          : new Error('Failed to accept classification')
      );
      throw error;
    } finally {
      this.isLoading$.next(false);
    }
  }

  /**
   * Analyze migration feasibility for a node using AI copilot.
   */
  async analyzeMigrationWithAI(
    node: WorkflowEngineNode,
    context: WorkflowContext,
    findings: CompatibilityIssue[]
  ): Promise<CopilotMigrationResult> {
    this.isLoading$.next(true);
    this.error$.next(null);

    try {
      return await this.workflowClient.analyzeMigrationWithAI(
        node,
        context,
        findings
      );
    } catch (error) {
      this.error$.next(
        error instanceof Error
          ? error
          : new Error('Failed to analyze migration')
      );
      throw error;
    } finally {
      this.isLoading$.next(false);
    }
  }

  // ===========================================================================
  // Private Helpers
  // ===========================================================================

  /**
   * Derive a ComplianceState summary from a full CompatibilityEvaluation.
   */
  private deriveComplianceState(
    evaluation: CompatibilityEvaluation
  ): ComplianceState {
    return {
      score: evaluation.complianceScore,
      status: evaluation.complianceStatus,
      requiresDiscoveryRun: evaluation.requiresDiscoveryRun,
      issueCount: evaluation.issues.length,
    };
  }
}
